import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import type { UserRole } from "@shared-types";

interface RoleGuardProps {
    /**
     * Roles allowed to render the wrapped children. The current user's
     * `role` must be in this list, otherwise the fallback is rendered.
     */
    requiredRoles: UserRole[];
    children: React.ReactNode;
    /**
     * Element rendered when the user is unauthenticated or has a role
     * outside `requiredRoles`. Defaults to a redirect to `/ytg` (the YTG
     * dashboard) — the natural landing page for a non-write user trying
     * to reach a write-only route.
     */
    fallback?: React.ReactNode;
}

/**
 * Reusable client-side role guard for protecting routes (or any subtree)
 * by user role.
 *
 * Authorization is still enforced server-side by the gateway (the RBAC
 * contract is the source of truth). This guard exists to prevent
 * non-write users from seeing UI that would 403 on submit — purely a UX
 * concern, not a security boundary.
 *
 * Server-side RBAC reference: gateway commit 56c3423 (restricts 17 YTG
 * mutation routes to admin+tm, 1 DELETE to admin only).
 *
 * @example
 *   <RoleGuard requiredRoles={["admin", "tm"]}>
 *       <CreateOperationPage />
 *   </RoleGuard>
 */
const RoleGuard = ({
    requiredRoles,
    children,
    fallback,
}: RoleGuardProps): JSX.Element => {
    const { user } = useAuth();

    // Unauthenticated, or role missing/not allowed → fallback.
    if (!user?.role || !requiredRoles.includes(user.role)) {
        if (fallback !== undefined) {
            return <>{fallback}</>;
        }
        return <Navigate to="/ytg" replace />;
    }

    return <>{children}</>;
};

export default RoleGuard;
